import { useMemo } from 'react';
import { useAppSelector } from '@/redux/hooks';
import { selectDataTableFilters, selectDataTableType } from '@/redux/selectors/dataTableSelectors';
import { NewFilterTypesMap } from '@/exports/columnCommonComponents';
import Filter, { FilterProps, FilterSelectorOptions } from '@/components/_common/Filters/Filter';
import FilterDateRange from '@/components/_common/Filters/FilterDateRange';
import SearchBar from '@/components/_common/SearchBar/SearchBar';
import FilterBoolean from '@/components/_common/Filters/FilterBoolean';
import FilterID from '@/components/_common/Filters/FilterID';
import FilterText from '@/components/_common/Filters/FilterText';
import FilterNullableBoolean from '@/components/_common/Filters/FilterNullableBoolean';
import FilterNumericRange from '@/components/_common/Filters/FilterNumericRange';
import { useConfigValue } from '@/components/_hooks/useConfigValue';
import { ClientConfig } from '@/__configs/config_types';
import { FilterType } from '@/components/_common/Filters/FilterDictionary';

type AppliedFiltersProps = {
  filters: FilterProps[];
  visibleQuickFilters?: FilterProps[];
};

const AppliedFilters = ({ filters, visibleQuickFilters }: AppliedFiltersProps) => {
  const filtersVersion = useConfigValue<ClientConfig['filtersVersion']>(['filtersVersion']);
  const dataTableFilters = useAppSelector(selectDataTableFilters);
  const tableType = useAppSelector(selectDataTableType);
  const visibleQuickFiltersIds = useMemo(() => (visibleQuickFilters ? visibleQuickFilters.map((filter) => filter.id) : []), [visibleQuickFilters]);

  return Object.keys(dataTableFilters).map((dtFilterKey) => {
    // do NOT display AppliedFilterChip if it exists in visibleQuickFilters config
    if (visibleQuickFiltersIds.includes(dtFilterKey) || dtFilterKey === 'search') return;
    if (tableType === 'calendar' && dtFilterKey === 'term_start') return; // hide term_start filter on calendar view

    const appliedFilters = dataTableFilters[dtFilterKey];
    const filterConfig = filters.find((filter) => filter.id === dtFilterKey);
    if (!filterConfig || !appliedFilters || appliedFilters?.length < 1) return;

    switch (filterConfig.type) {
      case NewFilterTypesMap.ARRAY: {
        return (
          <FilterSelectorOptions
            isQuickFilters
            key={filterConfig.id}
            type={filterConfig.id}
            searchable={filterConfig.searchable || (filterConfig.options && Object.keys(filterConfig.options)?.length > 5)}
            label={filterConfig.label}
            options={filterConfig.options}
            singleSelection={filterConfig.singleSelection}
            role={filterConfig.role}
          />
        );
      }
      case NewFilterTypesMap.SEARCH: {
        if (filtersVersion === 2) return;
        return <SearchBar key={filterConfig.id} role={filterConfig.role} />;
      }
      case NewFilterTypesMap.DATE: {
        if (filterConfig.field === 'term_start') return;
        return (
          <FilterDateRange
            isQuickFilters
            key={filterConfig.id}
            type={filterConfig.id}
            field={filterConfig.field}
            label={filterConfig.label}
            role={filterConfig.role}
          />
        );
      }
      case NewFilterTypesMap.BOOLEAN: {
        return <FilterBoolean key={filterConfig.id} type={filterConfig.id} label={filterConfig.label} role={filterConfig.role} isQuickFilters />;
      }
      case NewFilterTypesMap.USERS: {
        return (
          <Filter
            isQuickFilters
            key={filterConfig.id}
            type={filterConfig.id}
            component={filterConfig.component}
            searchable={filterConfig.searchable}
            label={filterConfig.label}
            options={filterConfig.options}
            singleSelection={filterConfig.singleSelection}
            renderApplied={filterConfig.renderApplied}
            role={filterConfig?.role}
          />
        );
      }
      case NewFilterTypesMap.ID: {
        return (
          <FilterID
            isQuickFilters
            key={filterConfig.id}
            type={filterConfig.id}
            label={filterConfig.label}
            excludeOptions={filterConfig.excludeOptions}
            options={filterConfig.options}
            role={filterConfig.role}
            behavior={filterConfig.filterBehavior}
          />
        );
      }
      case NewFilterTypesMap.TEXT: {
        return (
          <FilterText
            key={filterConfig.id}
            type={filterConfig.id}
            component={filterConfig.component}
            label={filterConfig.label}
            options={filterConfig.options}
            role={filterConfig.role}
          />
        );
      }
      case NewFilterTypesMap.NULLABLE_BOOLEAN: {
        return <FilterNullableBoolean key={filterConfig.label.toLowerCase().replace(/ /g, '_')} data={filterConfig as FilterType} />;
      }
      case NewFilterTypesMap.NUMERIC_RANGE: {
        return <FilterNumericRange key={filterConfig.id} type={filterConfig.id} label={filterConfig.label} limit={filterConfig.limit} />;
      }
      case NewFilterTypesMap.PROFILES: {
        return (
          <Filter
            isQuickFilters
            key={filterConfig.id}
            type={filterConfig.id}
            component={filterConfig.component}
            searchable={filterConfig.searchable}
            label={filterConfig.label}
            options={filterConfig.options}
            renderApplied={filterConfig.renderApplied}
          />
        );
      }
    }
  });
};

export default AppliedFilters;
